/* eslint-disable no-fallthrough */
import {
  ADD_TO_CART,
  ADD_BATCH_TO_CART,
  REMOVE_FROM_CART,
  ADD_ONE,
  ADD_MANY,
  REMOVE_ONE,
  SET_DISCOUNT,
  EMPTY_CART,
  UPDATE_CART_ITEM,
  SET_PENDING_ADD_PRODUCT_ID,
  IS_LOADING_ALL_PRODUCTS,
  IS_LOADING_ADD_PRODUCT,
} from '../types'

import { noWarranty } from 'Utils/constants'
import { getNextLineNum } from 'Utils/Helpers/getNextLineNum'

const initialState = {
  isLoadingAllProducts: false,
  isLoadingAddProduct: {
    active: false,
    id: null,
  },
  cart: [],
  pendingAddProductId: false,
}

function handleAddWarranty(product, quantity, cart) {
  // Search for the parent product by WarrantyProductId
  const matchingIndex = cart.findIndex(
    item =>
      item.ProductId === product.WarrantyProductId && !item.IsExtendedWarranty
  )
  const matchingProduct = matchingIndex !== -1 ? cart[matchingIndex] : null

  // Cap warranty qty at parent product qty
  const parentQty = matchingProduct
    ? Number(matchingProduct.Quantity)
    : quantity
  const warrantyQty = Math.min(quantity, parentQty)

  // If a warranty for this WarrantyProductId already exists, reuse its LineNum
  const existingWarrantyIndex = cart.findIndex(
    item =>
      item.IsExtendedWarranty &&
      item.WarrantyProductId === product.WarrantyProductId
  )

  const warrantyLineNum =
    existingWarrantyIndex !== -1
      ? cart[existingWarrantyIndex].LineNum
      : product.LineNum || getNextLineNum(cart)

  const newWarrantyLine = {
    ...product,
    LineNum: warrantyLineNum,
    Quantity: warrantyQty,
    QuoteLineId: '00000000-0000-0000-0000-000000000000',
    Reference: matchingProduct ? matchingProduct.ProductName : null,
  }

  // If a warranty for this WarrantyProductId already exists, replace it (update type or qty)
  if (existingWarrantyIndex !== -1) {
    const updatedCart = [...cart]
    updatedCart[existingWarrantyIndex] = newWarrantyLine
    // Sync WarrantyYears to parent product (mirrors the reset done on remove)
    if (matchingIndex !== -1 && product.WarrantyYears != null) {
      updatedCart[matchingIndex] = {
        ...updatedCart[matchingIndex],
        WarrantyYears: product.WarrantyYears,
      }
    }
    return updatedCart
  }

  // If there is no parent product, add at the end to avoid failure
  if (matchingIndex === -1) {
    return [...cart, newWarrantyLine]
  }

  // Insert immediately after the parent product, syncing WarrantyYears to parent
  const before = cart.slice(0, matchingIndex + 1)
  const after = cart.slice(matchingIndex + 1)
  if (product.WarrantyYears != null) {
    before[before.length - 1] = {
      ...before[before.length - 1],
      WarrantyYears: product.WarrantyYears,
    }
  }
  return [...before, newWarrantyLine, ...after]
}

function handleAddProduct(product, quantity, cart) {
  const hasLineNum = !!product.LineNum

  const index = cart.findIndex(p =>
    hasLineNum
      ? p.LineNum === product.LineNum && !p.IsExtendedWarranty
      : p.ProductId === product.ProductId && !p.IsExtendedWarranty
  )

  if (index !== -1) {
    return cart.map(p => {
      if (hasLineNum) {
        if (p.LineNum === product.LineNum && !p.IsExtendedWarranty) {
          return updateQuantity(product, quantity)
        }
        return p
      } else {
        if (p.ProductId === product.ProductId && !p.IsExtendedWarranty) {
          return updateQuantity(product, quantity)
        }
        return p
      }
    })
  }

  const newProduct = {
    ...product,
    Quantity: quantity,
    LineNum: product.LineNum || getNextLineNum(cart),
  }

  // If there is already an orphaned warranty for this product, insert the product before it
  const orphanedWarrantyIndex = cart.findIndex(
    item =>
      item.IsExtendedWarranty && item.WarrantyProductId === product.ProductId
  )
  if (orphanedWarrantyIndex !== -1) {
    return [
      ...cart.slice(0, orphanedWarrantyIndex),
      newProduct,
      ...cart.slice(orphanedWarrantyIndex),
    ]
  }

  return [...cart, newProduct]
}

function handleAddOne(product, cart) {
  // Warranty item: increment qty capped at parent product qty
  if (product.IsExtendedWarranty) {
    const parentProduct = cart.find(
      item =>
        !item.IsExtendedWarranty && item.ProductId === product.WarrantyProductId
    )
    const maxQty = parentProduct ? Number(parentProduct.Quantity) : Infinity
    return cart.map(item => {
      if (
        item.IsExtendedWarranty &&
        item.WarrantyProductId === product.WarrantyProductId
      ) {
        return updateQuantity(item, Math.min(Number(item.Quantity) + 1, maxQty))
      }
      return item
    })
  }

  const isWarrantyForProduct = item =>
    item.IsExtendedWarranty && item.WarrantyProductId === product.ProductId

  const hasWarranty = product.ProductId
    ? cart.some(isWarrantyForProduct)
    : false

  if (hasWarranty) {
    // Only increment product qty; warranty qty is not auto-increased (Rule 3)
    return cart.map(item => {
      if (item.LineNum === product.LineNum && !item.IsExtendedWarranty) {
        return updateQuantity(item, Number(item.Quantity) + 1)
      }
      return item
    })
  }

  return cart.map(p =>
    p.ProductId === product.ProductId &&
    !p.IsExtendedWarranty &&
    (!product.LineNum || p.LineNum === product.LineNum)
      ? updateQuantity(p, Number(p.Quantity) + 1)
      : p
  )
}

function handleAddMany(product, numberOfProd, cart) {
  // Warranty item: update qty capped at parent product qty
  if (product.IsExtendedWarranty) {
    const parentProduct = cart.find(
      item =>
        !item.IsExtendedWarranty && item.ProductId === product.WarrantyProductId
    )
    const maxQty = parentProduct ? Number(parentProduct.Quantity) : numberOfProd
    const cappedQty = Math.min(numberOfProd, maxQty)
    return cart.map(item =>
      item.IsExtendedWarranty &&
      item.WarrantyProductId === product.WarrantyProductId
        ? updateQuantity(item, cappedQty)
        : item
    )
  }

  const isWarrantyForProduct = item =>
    item.IsExtendedWarranty && item.WarrantyProductId === product.ProductId

  const hasWarranty = product.ProductId
    ? cart.some(isWarrantyForProduct)
    : false

  if (hasWarranty) {
    return cart.map(item => {
      if (item.LineNum === product.LineNum && !item.IsExtendedWarranty) {
        return updateQuantity(item, numberOfProd)
      }
      if (isWarrantyForProduct(item)) {
        // Cap warranty qty at new product qty (Rule 4), but don't auto-increase (Rule 3)
        return updateQuantity(
          item,
          Math.min(Number(item.Quantity), numberOfProd)
        )
      }
      return item
    })
  }

  return cart.map(p =>
    p.ProductId === product.ProductId &&
    !p.IsExtendedWarranty &&
    p.LineNum === product.LineNum
      ? updateQuantity(p, numberOfProd)
      : p
  )
}

function handleRemoveOne(product, newQuantity, cart) {
  if (newQuantity <= 0) {
    // Removing a warranty item: remove warranty only, keep parent product
    if (product.IsExtendedWarranty) {
      return cart
        .map(item => {
          if (item.IsExtendedWarranty) return item
          if (
            product.WarrantyProductId != null &&
            item.ProductId === product.WarrantyProductId
          ) {
            return { ...item, WarrantyYears: noWarranty.WarrantyYears }
          }
          return item
        })
        .filter(
          item =>
            !item.IsExtendedWarranty ||
            (product.WarrantyProductId == null
              ? item.LineNum !== product.LineNum
              : item.WarrantyProductId !== product.WarrantyProductId)
        )
    }

    // Removing a product: also remove its associated warranty
    if (product.LineNum) {
      return cart.filter(item => {
        if (item.IsExtendedWarranty) {
          return item.WarrantyProductId !== product.ProductId
        }
        return (
          item.LineNum !== product.LineNum ||
          item.ProductId !== product.ProductId
        )
      })
    }
    return cart.filter(item => {
      if (item.IsExtendedWarranty) {
        return item.WarrantyProductId !== product.ProductId
      }
      return item.ProductId !== product.ProductId
    })
  }

  // Warranty item: just update warranty qty
  if (product.IsExtendedWarranty) {
    return cart.map(item =>
      item.IsExtendedWarranty &&
      item.WarrantyProductId === product.WarrantyProductId
        ? updateQuantity(item, newQuantity)
        : item
    )
  }

  // Product with warranty: cap warranty qty at new product qty
  const isWarrantyForProduct = item =>
    item.IsExtendedWarranty && item.WarrantyProductId === product.ProductId

  const hasWarranty = product.ProductId
    ? cart.some(isWarrantyForProduct)
    : false

  if (hasWarranty) {
    return cart.map(item => {
      if (item.LineNum === product.LineNum && !item.IsExtendedWarranty) {
        return updateQuantity(item, newQuantity)
      }
      if (isWarrantyForProduct(item)) {
        return updateQuantity(
          item,
          Math.min(Number(item.Quantity), newQuantity)
        )
      }
      return item
    })
  }

  return cart.map(p =>
    p.ProductId === product.ProductId &&
    !p.IsExtendedWarranty &&
    (!product.LineNum || p.LineNum === product.LineNum)
      ? updateQuantity(p, newQuantity)
      : p
  )
}

function handleRemoveFromCart(removedProduct, cart) {
  const isWarranty = removedProduct.IsExtendedWarranty

  // Removing a warranty item: remove warranty line and reset parent's WarrantyYears
  if (isWarranty) {
    return cart
      .map(item => {
        if (item.IsExtendedWarranty) return item
        if (
          removedProduct.WarrantyProductId != null &&
          item.ProductId === removedProduct.WarrantyProductId
        ) {
          return { ...item, WarrantyYears: noWarranty.WarrantyYears }
        }
        return item
      })
      .filter(
        item =>
          !item.IsExtendedWarranty ||
          (removedProduct.WarrantyProductId == null
            ? item.LineNum !== removedProduct.LineNum
            : item.WarrantyProductId !== removedProduct.WarrantyProductId)
      )
  }

  // Removing a product by LineNum: also removes its associated warranty (via WarrantyProductId)
  if (removedProduct.LineNum) {
    return cart.filter(item => {
      if (item.IsExtendedWarranty) {
        return item.WarrantyProductId !== removedProduct.ProductId
      }
      return (
        item.LineNum !== removedProduct.LineNum ||
        item.ProductId !== removedProduct.ProductId
      )
    })
  }

  // Fallback (no LineNum): remove by ProductId and also clean up associated warranties
  return cart.filter(item => {
    if (item.IsExtendedWarranty) {
      return item.WarrantyProductId !== removedProduct.ProductId
    }
    return item.ProductId !== removedProduct.ProductId
  })
}

function handleAddBatch(items, initialCart) {
  let cart = initialCart
  for (const { product, qty } of items) {
    if (!product?.Price) continue
    cart = product.IsExtendedWarranty
      ? handleAddWarranty(product, qty, cart)
      : handleAddProduct(product, qty, cart)
  }
  return cart
}

function updateQuantity(p, quantity) {
  const DiscountAmount = Math.max(0, p.BasePrice - p.Price)
  const DiscountPct = Math.max(0, ((p.BasePrice - p.Price) * 100) / p.Price)
  const Tax = p.Price * quantity * p.TaxCategoryValue
  return {
    ...p,
    Quantity: quantity,
    qty: quantity,
    Amount: p.Price * quantity,
    Tax,
    AmountWithTax: p.Price * quantity + Tax,
    DiscountAmount,
    DiscountAmountLocal: DiscountAmount,
    DiscountPct,
  }
}

export default function cartReducer(state = initialState, action) {
  switch (action.type) {
    case ADD_TO_CART: {
      const { product, qty } = action.payload
      if (!product?.Price) return state
      const cart = product.IsExtendedWarranty
        ? handleAddWarranty(product, qty, state.cart)
        : handleAddProduct(product, qty, state.cart)
      return { ...state, cart }
    }

    case REMOVE_FROM_CART: {
      const removedProduct = action.payload.product
      const cart = handleRemoveFromCart(removedProduct, state.cart)
      return { ...state, cart }
    }

    case ADD_ONE:
      return {
        ...state,
        cart: handleAddOne(action.payload.product, state.cart),
      }

    case ADD_MANY:
      return {
        ...state,
        cart: handleAddMany(
          action.payload.product,
          action.payload.numberOfProd,
          state.cart
        ),
      }

    case REMOVE_ONE:
      return {
        ...state,
        cart: handleRemoveOne(
          action.payload.product,
          action.payload.newQuantity,
          state.cart
        ),
      }

    case SET_DISCOUNT: {
      const { product } = action.payload

      if (product?.LineNum) {
        const cart = state.cart.map(p =>
          p.LineNum === product.LineNum && !p.IsExtendedWarranty
            ? {
                ...product,
                isPriceChanged:
                  'isPriceChanged' in product
                    ? product.isPriceChanged
                    : p.isPriceChanged,
              }
            : p
        )
        return { ...state, cart }
      }

      const cart = state.cart.map(p =>
        p.ProductId === product.ProductId && !p.IsExtendedWarranty
          ? {
              ...product,
              isPriceChanged:
                'isPriceChanged' in product
                  ? product.isPriceChanged
                  : p.isPriceChanged,
            }
          : p
      )
      return { ...state, cart }
    }

    case ADD_BATCH_TO_CART: {
      const cart = handleAddBatch(action.payload, state.cart)
      return { ...state, cart }
    }

    case EMPTY_CART:
      return { ...state, cart: [] }

    case IS_LOADING_ALL_PRODUCTS:
      return {
        ...state,
        isLoadingAllProducts: action.payload.isLoadingAllProducts,
      }
    case IS_LOADING_ADD_PRODUCT:
      return {
        ...state,
        isLoadingAddProduct: action.payload.isLoadingAddProduct,
      }

    case 'IS_PRICE_CHANGED': {
      const { index, value } = action.payload
      const cart = [...state.cart]
      cart[index] = { ...cart[index], isPriceChanged: value }
      return { ...state, cart }
    }

    case UPDATE_CART_ITEM: {
      const { updatedFields, matchCriteria, productId } = action.payload
      return {
        ...state,
        cart: state.cart.map(item => {
          const isMatch = matchCriteria
            ? Object.entries(matchCriteria).every(([key, val]) => {
                const itemVal =
                  typeof item[key] === 'boolean'
                    ? !!item[key]
                    : String(item[key])
                const matchVal = typeof val === 'boolean' ? !!val : String(val)
                return itemVal === matchVal
              })
            : item.ProductId === productId
          return isMatch ? { ...item, ...updatedFields } : item
        }),
      }
    }

    case SET_PENDING_ADD_PRODUCT_ID:
      return {
        ...state,
        pendingAddProductId: action.payload,
      }

    default:
      return state
  }
}
